import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@monksflow/monks-ui';
import { TableFilterIcon } from '../TableFilterIcon';
import type { FilterConfig } from '../Table.types';

interface TableFilterMenuProps<TFilters> {
  /** Options for the column this menu filters. Omitted when the column has no config. */
  config?: FilterConfig<TFilters>;
  filters: TFilters;
  onFiltersChange: (filters: TFilters) => void;
  /** Column name, used for the trigger's accessible label. */
  label: string;
}

function isSelected(current: unknown, value: string): boolean {
  return Array.isArray(current) ? current.includes(value) : current === value;
}

function hasSelection(current: unknown): boolean {
  if (Array.isArray(current)) return current.length > 0;
  if (typeof current === 'string') return current.length > 0;
  return false;
}

export function TableFilterMenu<TFilters>({
  config,
  filters,
  onFiltersChange,
  label,
}: TableFilterMenuProps<TFilters>) {
  if (!config) return null;

  const current = filters[config.key];

  const handleToggle = (value: string) => {
    if (Array.isArray(current)) {
      const values = current as string[];
      const updated = values.includes(value)
        ? values.filter((v) => v !== value)
        : values.concat(value);
      onFiltersChange({ ...filters, [config.key]: updated });
      return;
    }
    if (typeof current === 'string') {
      onFiltersChange({ ...filters, [config.key]: current === value ? '' : value });
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <TableFilterIcon label={label} isActive={hasSelection(current)} />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-60">
        {config.options.map((option) => (
          <DropdownMenuCheckboxItem
            key={option.value}
            checked={isSelected(current, option.value)}
            // Toggling one option should not dismiss the list — filtering several
            // values in a row is the normal case.
            onSelect={(event) => {
              event.preventDefault();
            }}
            onCheckedChange={() => handleToggle(option.value)}
          >
            {option.label}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
